import { useParams } from 'react-router';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IWarningRatification } from '../../../../../../../models/interfaces/WarningRatification';
import { IEditSubscriptionDetailsPayload } from '../../../../../../../store/saga-slices/subscriptions/airAmbulance/models';
import { EDITING_SUBSCRIPTION_DETAILS_MODAL_NAME } from './subscriptionEditDetailsModal.consts';
import { useProcessing } from '../../../../../../../hooks/useProcessing';
import { useActions } from '../../../../../../../hooks/useActions';
import { updateModalState } from '../../../../../../../store/redux-slices/modals';
import { editAASubscriptionDetails } from '../../../../../../../store/saga-slices/subscriptions/airAmbulance';
import { SUBSCRIPTION_DETAILS_EDITING_PROCESSING } from '../../../../../../../store/redux-slices/processes/constants';
import { aaSubscriptionOverviewSelector } from '../../../../../../../store/redux-slices/subscriptions/airAmbulance/selectors';
import { organizationNameSelector } from '../../../../../../../store/redux-slices/organizations/selectors';
import { useFormErrorsActions } from '../../../../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

import { AirAmbulanceSubscriptionOptions } from '../../../../../../../models/enums/AirAmbulanceSubscriptionOptions';
import { useApiResponseFail } from '../../../../../../../hooks/useApiResponseFail';
import { IAAOrganizationSubscriptionOptionsModel } from '../../../../../../../models/interfaces/Subscription/AirAmbulance/AAOrganizationSubscriptionOptionsModel';

type UseProductSubscriptionEditDetailsModalType = {
  onModalSuccess: () => void;
};

export const useProductSubscriptionEditDetailsModal = ({
  onModalSuccess,
}: UseProductSubscriptionEditDetailsModalType) => {
  const alert = useAlert();
  const { t } = useTranslation();

  const UpdateModalState = useActions(updateModalState);
  const EditSubscriptionDetails = useActions(editAASubscriptionDetails);

  const subscriptionEditing = useProcessing(
    SUBSCRIPTION_DETAILS_EDITING_PROCESSING,
  );

  const subscriptionOverview = useSelector(aaSubscriptionOverviewSelector);
  const organizationName = useSelector(organizationNameSelector);

  const { id: organizationId, subscriptionId } = useParams();

  const { validateErrors } = useFormErrorsActions();
  const { handleResponse } = useApiResponseFail();

  const handleSubmit = (values: IAAOrganizationSubscriptionOptionsModel) => {
    editOrganizationSubscription(values);
  };

  const editOrganizationSubscription = (
    values: IAAOrganizationSubscriptionOptionsModel,
    ratifications: IWarningRatification[] = [],
  ) => {
    const payload: IEditSubscriptionDetailsPayload = {
      organizationId,
      subscriptionId,
      data: values,
      success: () => {
        alert.success(
          t('alerts.subscription-edit-success', {
            value: organizationName,
          }),
        );
        onModalSuccess && onModalSuccess();
        UpdateModalState(EDITING_SUBSCRIPTION_DETAILS_MODAL_NAME);
      },
      error: (err) => {
        handleResponse({
          err,
          values,
          ratifications,
          alertErrorMessage: t('alerts.subscription-edit-fail', {
            value: organizationName,
          }),
        });
      },
    };
    EditSubscriptionDetails(payload);
  };

  const handleWarningsConfirm = (
    data: IAAOrganizationSubscriptionOptionsModel,
    warnings: IWarningRatification[],
  ) => {
    UpdateModalState([EDITING_SUBSCRIPTION_DETAILS_MODAL_NAME, { data }]);
    editOrganizationSubscription(data, warnings);
  };

  const onCancelHandler = () =>
    UpdateModalState(EDITING_SUBSCRIPTION_DETAILS_MODAL_NAME);

  const initData = subscriptionOverview && {
    [AirAmbulanceSubscriptionOptions.AutoRenewal]:
      subscriptionOverview.autoRenewal,
  };

  return {
    handleWarningsConfirm,
    handleSubmit,
    validateErrors,
    UpdateModalState,
    subscriptionEditing,
    initData,
    onCancelHandler,
  };
};
